import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Typography, Grid, Card, CardContent, Chip,
  Table, TableBody, TableCell, TableHead, TableRow,
  CircularProgress, Alert,
} from '@mui/material';
import { fetchPurchaseOrders, fetchSalesOrders, fetchShippingAdvisory } from '../../store/slices/supplyChainSlice';
import { AppDispatch, RootState } from '../../store/store';

const dayColor = (delta: number | null) => {
  if (delta === null) return 'default';
  if (delta <= 0) return 'success';
  if (delta <= 7) return 'warning';
  return 'error';
};

const SupplyChainOverview: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { purchaseOrders, salesOrders, shippingAdvisory } = useSelector((s: RootState) => s.supplyChain);

  useEffect(() => {
    dispatch(fetchPurchaseOrders({ status: 'open' }));
    dispatch(fetchSalesOrders({ status: 'pendingFulfillment' }));
    dispatch(fetchShippingAdvisory());
  }, [dispatch]);

  const atRiskCount = shippingAdvisory.data.filter(s => (s.daysDelta || 0) > 0).length;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>Supply Chain Overview</Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Open Purchase Orders</Typography>
              <Typography variant="h3">
                {purchaseOrders.loading ? <CircularProgress size={24} /> : purchaseOrders.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>Open Sales Orders</Typography>
              <Typography variant="h3">
                {salesOrders.loading ? <CircularProgress size={24} /> : salesOrders.total}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card sx={{ bgcolor: atRiskCount > 0 ? 'error.light' : 'inherit' }}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>At-Risk Orders</Typography>
              <Typography variant="h3" color={atRiskCount > 0 ? 'error.dark' : 'inherit'}>
                {shippingAdvisory.loading ? <CircularProgress size={24} /> : atRiskCount}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Typography variant="h6" gutterBottom>Shipping Advisory</Typography>
      {shippingAdvisory.error && (
        <Alert severity="error" sx={{ mb: 2 }}>{shippingAdvisory.error}</Alert>
      )}
      {shippingAdvisory.loading ? <CircularProgress /> : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>SO Number</TableCell>
              <TableCell>Customer</TableCell>
              <TableCell>Priority</TableCell>
              <TableCell>Requested Ship</TableCell>
              <TableCell>Committed Ship</TableCell>
              <TableCell>Delta (days)</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {shippingAdvisory.data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography color="textSecondary">No at-risk orders</Typography>
                </TableCell>
              </TableRow>
            ) : shippingAdvisory.data.map(item => (
              <TableRow key={item.soId}>
                <TableCell><strong>{item.soNumber}</strong></TableCell>
                <TableCell>{item.customerName}</TableCell>
                <TableCell>
                  <Chip label={item.priority} size="small" color={item.priority === 'high' ? 'error' : 'default'} />
                </TableCell>
                <TableCell>{item.requestedShipDate ? new Date(item.requestedShipDate).toLocaleDateString() : '—'}</TableCell>
                <TableCell>{item.committedShipDate ? new Date(item.committedShipDate).toLocaleDateString() : '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={item.daysDelta !== null ? (item.daysDelta > 0 ? '+' + item.daysDelta : item.daysDelta) : '—'}
                    size="small"
                    color={dayColor(item.daysDelta) as any}
                  />
                </TableCell>
                <TableCell>
                  <Chip label={item.atpFeasible ? 'On Time' : 'Late'} size="small" color={item.atpFeasible ? 'success' : 'error'} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default SupplyChainOverview;
